import { inject } from '@angular/core';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';

import { RetailerProgramsApi } from '@core/api';
import * as Entities from '@shared/entities';
import { Result } from '@shared/models';
import { extractErrorMessage } from '@shared/utils';
import { commandResult, failureResult } from './store-helpers';

type ProviderServiceKeyUpdateRequest = {
  retailerProgramId: number;
  addProviderServiceKeyIds: number[];
  removeProviderServiceKeyIds: number[];
};

export const RetailerProgramsStore = signalStore(
  { providedIn: 'root' },
  withState({
    providerServiceKeys: [] as Entities.ProviderServiceKey[],
    loadingProviderServiceKeys: false,
    updatingProviderServiceKeys: false,
    providerServiceKeyRetailerProgramId: null as number | null,
    errorMessage: null as string | null,
    lastCommandName: null as string | null,
    lastCommandResult: null as Result<unknown> | null,
  }),
  withMethods((store, api = inject(RetailerProgramsApi)) => {
    let listRequestId = 0;

    const listProviderServiceKeys = (retailerProgramId: number): void => {
      const requestId = ++listRequestId;
      patchState(store, {
        loadingProviderServiceKeys: true,
        errorMessage: null,
        providerServiceKeyRetailerProgramId: retailerProgramId,
      });

      void api
        .listProviderServiceKeys(retailerProgramId)
        .then((result) => {
          if (requestId === listRequestId) {
            patchState(store, {
              providerServiceKeys: result.successful ? (result.data.records ?? []) : [],
              errorMessage: result.successful ? null : result.message,
              lastCommandName: 'listProviderServiceKeys',
              lastCommandResult: commandResult('listProviderServiceKeys', result),
            });
          }
        })
        .catch((error: unknown) => {
          if (requestId === listRequestId) {
            const message = extractErrorMessage(error, 'Unable to load retailer program provider service keys.');
            patchState(store, {
              providerServiceKeys: [],
              errorMessage: message,
              lastCommandName: 'listProviderServiceKeys',
              lastCommandResult: commandResult('listProviderServiceKeys', failureResult(message)),
            });
          }
        })
        .finally(() => {
          if (requestId === listRequestId) {
            patchState(store, { loadingProviderServiceKeys: false });
          }
        });
    };

    const updateProviderServiceKeysByRequest = (request: ProviderServiceKeyUpdateRequest): void => {
      if (store.updatingProviderServiceKeys()) {
        return;
      }

      patchState(store, {
        updatingProviderServiceKeys: true,
        errorMessage: null,
        lastCommandName: 'updateProviderServiceKeys',
        lastCommandResult: null,
      });

      const addRequests: Array<() => ReturnType<RetailerProgramsApi['addProviderServiceKey']>> =
        request.addProviderServiceKeyIds.map((providerServiceKeyId) => {
          return (): ReturnType<RetailerProgramsApi['addProviderServiceKey']> =>
            api.addProviderServiceKey(request.retailerProgramId, providerServiceKeyId);
        });
      const removeRequests: Array<() => ReturnType<RetailerProgramsApi['removeProviderServiceKey']>> =
        request.removeProviderServiceKeyIds.map((providerServiceKeyId) => {
          return (): ReturnType<RetailerProgramsApi['removeProviderServiceKey']> =>
            api.removeProviderServiceKey(request.retailerProgramId, providerServiceKeyId);
        });
      const providerServiceKeyRequests: Array<() => ReturnType<RetailerProgramsApi['addProviderServiceKey']>> = [
        ...addRequests,
        ...removeRequests,
      ];

      void providerServiceKeyRequests
        .reduce(
          (chain, nextRequest) => {
            return chain.then((results) => nextRequest().then((result) => [...results, result]));
          },
          Promise.resolve([] as Result<void>[])
        )
        .then((results) => {
          const failedResult = results.find((result) => !result.successful);
          const result = failedResult
            ? failureResult(failedResult.message || 'Unable to update retailer program provider service keys.', false)
            : Result.success(true);
          patchState(store, {
            lastCommandResult: commandResult('updateProviderServiceKeys', result),
            errorMessage: result.successful ? null : result.message,
          });
          if (result.successful) {
            listProviderServiceKeys(request.retailerProgramId);
          }
        })
        .catch((error: unknown) => {
          const message = extractErrorMessage(error, 'Unable to update retailer program provider service keys.');
          patchState(store, {
            errorMessage: message,
            lastCommandResult: commandResult('updateProviderServiceKeys', failureResult(message)),
          });
        })
        .finally(() => {
          patchState(store, { updatingProviderServiceKeys: false });
        });
    };

    return {
      listProviderServiceKeys,
      addProviderServiceKey(retailerProgramId: number, providerServiceKeyId: number): void {
        if (store.updatingProviderServiceKeys()) {
          return;
        }

        patchState(store, {
          updatingProviderServiceKeys: true,
          errorMessage: null,
          lastCommandName: 'addProviderServiceKey',
          lastCommandResult: null,
        });

        void api
          .addProviderServiceKey(retailerProgramId, providerServiceKeyId)
          .then((result) => {
            patchState(store, {
              lastCommandResult: commandResult('addProviderServiceKey', result),
              errorMessage: result.successful ? null : result.message,
            });
            if (result.successful) {
              listProviderServiceKeys(retailerProgramId);
            }
          })
          .catch((error: unknown) => {
            const message = extractErrorMessage(
              error,
              'Unable to addProviderServiceKey retailer program provider service key.'
            );
            patchState(store, {
              errorMessage: message,
              lastCommandResult: commandResult('addProviderServiceKey', failureResult(message)),
            });
          })
          .finally(() => {
            patchState(store, { updatingProviderServiceKeys: false });
          });
      },
      removeProviderServiceKey(retailerProgramId: number, providerServiceKeyId: number): void {
        if (store.updatingProviderServiceKeys()) {
          return;
        }

        patchState(store, {
          updatingProviderServiceKeys: true,
          errorMessage: null,
          lastCommandName: 'removeProviderServiceKey',
          lastCommandResult: null,
        });

        void api
          .removeProviderServiceKey(retailerProgramId, providerServiceKeyId)
          .then((result) => {
            patchState(store, {
              lastCommandResult: commandResult('removeProviderServiceKey', result),
              errorMessage: result.successful ? null : result.message,
            });
            if (result.successful) {
              listProviderServiceKeys(retailerProgramId);
            }
          })
          .catch((error: unknown) => {
            const message = extractErrorMessage(
              error,
              'Unable to removeProviderServiceKey retailer program provider service key.'
            );
            patchState(store, {
              errorMessage: message,
              lastCommandResult: commandResult('removeProviderServiceKey', failureResult(message)),
            });
          })
          .finally(() => {
            patchState(store, { updatingProviderServiceKeys: false });
          });
      },
      updateProviderServiceKeys(
        retailerProgramId: number,
        addProviderServiceKeyIds: number[],
        removeProviderServiceKeyIds: number[]
      ): void {
        updateProviderServiceKeysByRequest({
          retailerProgramId,
          addProviderServiceKeyIds,
          removeProviderServiceKeyIds,
        });
      },
      clearCommandResult(): void {
        patchState(store, { lastCommandName: null, lastCommandResult: null });
      },
    };
  })
);
